import React from 'react';
import PropTypes from 'prop-types';

/**
 * Component to format schedule data into human-readable text
 * @param {Object} props - Component props
 * @param {Object} props.schedule - Schedule data from backend
 * @returns {JSX.Element} Formatted schedule text
 */
const ScheduleFormatter = ({schedule}) => {
  if (!schedule) {
    return <span>No schedule configured</span>;
  }

  const {hour, minute, interval, interval_count, timezone} = schedule;

  // Format time with leading zeros
  const formattedTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

  // Get day name if it's a weekly schedule
  const getDayName = (dayCode) => {
    const days = {
      'SUNDAY': 'Sunday',
      'MONDAY': 'Monday',
      'TUESDAY': 'Tuesday',
      'WEDNESDAY': 'Wednesday',
      'THURSDAY': 'Thursday',
      'FRIDAY': 'Friday',
      'SATURDAY': 'Saturday'
    };
    return days[dayCode] || dayCode;
  };

  // Format the schedule based on interval type
  let formattedSchedule = '';

  switch (interval) {
    case 'DAILY':
      formattedSchedule = `Every day at ${formattedTime}`;
      break;

    case 'HOURLY':
      if (interval_count === '1') {
        formattedSchedule = `Every hour at ${minute} minutes past the hour starting at ${hour}:${minute}`;
      } else {
        formattedSchedule = `Every ${interval_count} hours at ${minute} minutes starting at ${hour}:${minute}`;
      }
      break;

    case 'WEEKLY':
      const dayName = getDayName(interval_count);
      formattedSchedule = `Every ${dayName} at ${formattedTime}`;
      break;

    default:
      formattedSchedule = 'Invalid schedule configuration';
  }

  // Add timezone info if available and not UTC
  if (timezone && timezone !== 'UTC') {
    formattedSchedule += ` (${timezone})`;
  }

  return <>{formattedSchedule}</>;
};

ScheduleFormatter.propTypes = {
  schedule: PropTypes.shape({
    hour: PropTypes.number.isRequired,
    minute: PropTypes.number.isRequired,
    interval: PropTypes.string.isRequired,
    interval_count: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired
  })
};

export default ScheduleFormatter;
